import React, { memo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Switch, Route, useHistory } from "react-router-dom";

import { Layout, Typography, Row, Button } from "antd";
import { isValidElementType } from "react-is";

import useWindowResize from "hooks/useWindowResize";
import useGetCurrentRouteInfo from "hooks/useGetCurrentRouteInfo";
import Router from "models/Router";

import selectors from "rdx/selectors";

import { PowurLogoIcon, PowurLogoFullIcon, PowurLogoMobileIcon, PowurLogoXIcon } from "components/Icons";
import Banners from "components/PlatformLayout/components/Banners";
import InjectUserFlow from "components/Injectors/InjectUserFlow";
import InjectZendeskWidget from "components/Injectors/InjectZendeskWidget";
import FEATURE_FLAGS from "@flags";
import { FeatureFlags } from "types/utils/featureFlags";
import PlatformModals from "../PlatformModals";
import Logout from "./UserLogout";
import ThemeToggle from "./ThemeToggle";

import styles from "./PlatformPageHeader.module.less";
import AddLeadButton from "./AddLeadButton";

const { Header } = Layout;
const { Title } = Typography;

const PlatformPageHeader = (props) => {
  const { mainNavCollapsed, toggleMenu, altLogout, altLogo } = props;

  const { platformRoutes: routes } = useGetCurrentRouteInfo();
  const { width } = useWindowResize();
  const history = useHistory();

  const showBackButton = history.location.state?.from === "/admin-reports/activity-log";

  const bannersHeight = useSelector(selectors.selectBannersHeight);

  const icon = () => {
    if (toggleMenu === null) {
      return <></>;
    }

    if (altLogo) return altLogo;

    return (
      <div
        data-test-id="PlatformPageHeader-logo"
        className={mainNavCollapsed ? styles.logoContainer : styles.logoContainerOpen}
        onMouseOver={toggleMenu}
        onFocus={toggleMenu}
        onClick={toggleMenu}
        onKeyDown={toggleMenu}
        tabIndex={0}
        role="button"
      >
        {width <= 500 ? (
          <>{mainNavCollapsed ? <PowurLogoMobileIcon /> : <PowurLogoXIcon />}</>
        ) : (
          <>
            {mainNavCollapsed ? (
              <PowurLogoIcon />
            ) : (
              <>
                <PowurLogoFullIcon className={styles.icon} />
                <span className={styles.platformLabel}>PLATFORM</span>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <Banners />
      <Header
        className={styles.header}
        style={{
          top: `${bannersHeight}px`,
        }}
      >
        <Row className={styles.row} type="flex" justify="space-between" align="middle">
          <div className={styles.headerLeft}>
            {icon()}
            {showBackButton && (
              <Button className={styles.backButton} onClick={() => history.goBack()}>
                Back
              </Button>
            )}
            <Switch>
              {routes.map((r) => (
                <Route
                  {...r}
                  render={() => {
                    if (typeof r.title === "string") {
                      return (
                        <Title
                          level={4}
                          className={width > 340 ? styles.title : styles.hide}
                          style={{ fontWeight: 600 }}
                        >
                          {r.title}
                        </Title>
                      );
                    }

                    if (isValidElementType(r.title)) {
                      return <r.title />;
                    }

                    return null;
                  }}
                />
              ))}
            </Switch>
          </div>
          <div className={styles.headerActions}>
            {FEATURE_FLAGS.includes(FeatureFlags.THEME_TOGGLE) && <ThemeToggle />}
            <AddLeadButton />
            <span className={styles.zdChatContainer}>
              <InjectZendeskWidget displayWidget />
            </span>
            {altLogout || <Logout />}
          </div>
          <PlatformModals />
        </Row>
        <InjectUserFlow />
      </Header>
    </>
  );
};

PlatformPageHeader.propTypes = {
  routes: PropTypes.arrayOf(Router.authRouteTypes()),
  mainNavCollapsed: PropTypes.bool,
  altLogout: PropTypes.node,
  altLogo: PropTypes.node,
  toggleMenu: PropTypes.func,
};

export default memo(PlatformPageHeader);
